import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Plus, Users, Loader2 } from "lucide-react";

export default function TeacherClasses() {
  const [classes, setClasses] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get("/classes").then((r) => setClasses(r.data));
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/classes", { name, description: desc });
      setName("");
      setDesc("");
      setShowNew(false);
      load();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title="Classes" subtitle="Group students together and assign exams.">
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowNew(!showNew)} className="btn-primary text-sm" data-testid="new-class-toggle">
          <Plus className="w-4 h-4" /> New class
        </button>
      </div>
      {showNew && (
        <form onSubmit={create} className="qs-card p-6 mb-6" data-testid="new-class-form">
          <h3 className="font-display text-lg font-bold text-slate-900 mb-3">Create class</h3>
          <div className="space-y-3">
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Class name (e.g. Grade 8 Section B)"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              data-testid="new-class-name"
            />
            <input
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Description (optional)"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              data-testid="new-class-desc"
            />
            {err && <p className="text-sm text-rose-600">{err}</p>}
            <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="new-class-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
            </button>
          </div>
        </form>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="class-list">
        {classes.map((c) => (
          <div key={c.id} className="qs-card p-5" data-testid={`class-card-${c.id}`}>
            <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 grid place-items-center">
              <Users className="w-5 h-5" />
            </div>
            <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{c.name}</h3>
            <p className="text-sm text-slate-500 line-clamp-2">{c.description || "—"}</p>
            <p className="mt-3 text-xs text-slate-500">
              {(c.student_ids?.length || 0)} students · Teacher: {c.teacher_name || "—"}
            </p>
          </div>
        ))}
        {classes.length === 0 && (
          <p className="col-span-full text-center text-slate-500 py-12" data-testid="no-classes">No classes yet.</p>
        )}
      </div>
    </AppShell>
  );
}
